import React, {useContext, useRef} from 'react';
import {communityContext} from '@ark/react';
import {SearchSelectValue} from './SearchSelect';
import {
  CommunitySelect,
  CommunitySelectProps,
} from './CommunitySelect/CommunitySelect';

import {track} from '@amplitude/analytics-browser';

interface CommunityFilterContextType {
  value: SearchSelectValue;
  setValue: React.Dispatch<React.SetStateAction<SearchSelectValue>>;
  disabled: boolean;
  initialised: boolean;
}

export const CommunityFilterContext =
  React.createContext<CommunityFilterContextType>({
    value: [],
    setValue: () => {},
    disabled: false,
    initialised: false,
  });

type CommunityFilterProviderProps = {
  children: React.ReactNode;
};

export const useCommunityFilter = () => {
  const context = useContext(CommunityFilterContext);
  if (!context) {
    throw new Error(
      'useCommunityFilter must be used within a CommunityFilterProvider',
    );
  }
  return context;
};

export const CommunityFilterProvider = ({
  children,
}: CommunityFilterProviderProps) => {
  const [value, setValue] = React.useState<SearchSelectValue>([]);
  const [initialised, setInitialised] = React.useState(false);
  const {communities} = React.useContext(communityContext);
  const initializedRef = useRef(false);

  const disabled = Boolean(communities && communities.length === 1);

  // Seed the filter with every community once they first become available.
  // Only run once — subsequent re-renders pass a new `communities` array
  // reference on every navigation, and re-applying this effect would wipe
  // the user's selection.
  React.useEffect(() => {
    if (initializedRef.current) return;
    if (communities && communities.length > 0) {
      setValue(
        communities.map(community => ({
          value: community.id,
          label: community.name!,
        })),
      );
      initializedRef.current = true;
      setInitialised(true);
    }
  }, [communities]);

  return (
    <CommunityFilterContext.Provider
      value={{value, setValue, disabled, initialised}}
    >
      {children}
    </CommunityFilterContext.Provider>
  );
};

export function useSelectedCommunities(): string[] {
  const {value} = React.useContext(CommunityFilterContext);

  const communities = value.map(x => x.value);
  return communities;
}

type CommunityFilterProps = {
  fullWidth?: boolean;
  onChange?: CommunitySelectProps['onChange'];
};

export const CommunityFilter = (props: CommunityFilterProps) => {
  const {value, setValue, disabled} = React.useContext(CommunityFilterContext);
  return (
    <CommunitySelect
      fullWidth={props.fullWidth}
      disabled={disabled}
      data-attr="community-filter-select"
      value={value}
      onChange={v => {
        props.onChange?.(v);
        track('Community Filter', {
          Selected: value.map(v => v.label),
        });
        setValue(v);
      }}
    />
  );
};
